import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { submitContactForm, submitQuoteRequest } from '../api/client';

const ContactPage = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    propertyType: '',
    squareFootage: '',
    message: ''
  });

  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    
    try {
      await submitContactForm(contactForm);
      toast.success('Thank you! We will get back to you within 24 hours.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingQuote(true);
    
    try {
      await submitQuoteRequest(quoteForm);
      toast.success('Quote request received! We will send you a customized estimate soon.');
      setQuoteForm({ name: '', email: '', phone: '', serviceType: '', propertyType: '', squareFootage: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
      console.error('Quote form error:', error);
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Get In <span className="text-red-600">Touch</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We're here to answer your questions and provide a free quote for your cleaning needs
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Contact Info Cards */}
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Email Us</h3>
                <a href="mailto:tpcslex@gmail.com" className="text-red-600 hover:text-red-700">
                  tpcslex@gmail.com
                </a>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Service Area</h3>
                <p className="text-gray-600">
                  Lexington, KY and surrounding cities
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact and Quote Forms */}
          <Card className="max-w-4xl mx-auto border-gray-200">
            <CardHeader>
              <CardTitle className="text-2xl">Contact Us</CardTitle>
              <CardDescription>Send us a message or request a free quote</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="contact">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="contact">Contact Form</TabsTrigger>
                  <TabsTrigger value="quote">Request Quote</TabsTrigger>
                </TabsList>

                {/* Contact Form */}
                <TabsContent value="contact">
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact-name">Full Name *</Label>
                        <Input
                          id="contact-name"
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-email">Email *</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone">Phone Number</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-message">Message *</Label>
                      <Textarea
                        id="contact-message"
                        required
                        rows={5}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        placeholder="How can we help you?"
                      />
                    </div>
                    <Button type="submit" disabled={isSubmittingContact} className="bg-red-600 hover:bg-red-700 text-white w-full">
                      {isSubmittingContact ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </TabsContent>

                {/* Quote Form */}
                <TabsContent value="quote">
                  <form onSubmit={handleQuoteSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="quote-name">Full Name *</Label>
                        <Input
                          id="quote-name"
                          required
                          value={quoteForm.name}
                          onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quote-email">Email *</Label>
                        <Input
                          id="quote-email"
                          type="email"
                          required
                          value={quoteForm.email}
                          onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="quote-phone">Phone Number *</Label>
                        <Input
                          id="quote-phone"
                          type="tel"
                          required
                          value={quoteForm.phone}
                          onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service-type">Service Type *</Label>
                        <Select value={quoteForm.serviceType} onValueChange={(value) => setQuoteForm({ ...quoteForm, serviceType: value })}>
                          <SelectTrigger id="service-type">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="residential">Residential Cleaning</SelectItem>
                            <SelectItem value="commercial">Commercial Cleaning</SelectItem>
                            <SelectItem value="deep">Deep Cleaning</SelectItem>
                            <SelectItem value="move">Move-In/Move-Out</SelectItem>
                            <SelectItem value="airbnb">Air BnB Cleaning</SelectItem>
                            <SelectItem value="postconstruction">Post Construction Cleaning</SelectItem>
                            <SelectItem value="retail">Retail Store Cleaning</SelectItem>
                            <SelectItem value="gym">Gym Cleaning</SelectItem>
                            <SelectItem value="warehouse">Warehouse Cleaning</SelectItem>
                            <SelectItem value="restaurant">Restaurant Cleaning</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="property-type">Property Type</Label>
                        <Select value={quoteForm.propertyType} onValueChange={(value) => setQuoteForm({ ...quoteForm, propertyType: value })}>
                          <SelectTrigger id="property-type">
                            <SelectValue placeholder="Select property type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="house">House</SelectItem>
                            <SelectItem value="apartment">Apartment</SelectItem>
                            <SelectItem value="condo">Condo</SelectItem>
                            <SelectItem value="office">Office</SelectItem>
                            <SelectItem value="retail">Retail Space</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="square-footage">Approximate Square Footage</Label>
                        <Input
                          id="square-footage"
                          value={quoteForm.squareFootage}
                          onChange={(e) => setQuoteForm({ ...quoteForm, squareFootage: e.target.value })}
                          placeholder="e.g., 1500"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote-message">Additional Details</Label>
                      <Textarea
                        id="quote-message"
                        rows={4}
                        value={quoteForm.message}
                        onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                        placeholder="Tell us about your cleaning needs..."
                      />
                    </div>
                    <Button type="submit" disabled={isSubmittingQuote} className="bg-red-600 hover:bg-red-700 text-white w-full">
                      {isSubmittingQuote ? 'Submitting...' : 'Request Free Quote'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
